// Shared primitives for interpreting raw game transactions returned by the
// analytics `/gametransactions` endpoint. BetHistory renders them as round
// cards and the sports settlement audit reconciles them against a transaction
// period — both must agree on stakes, payouts and provider quirks, so the
// classification lives here rather than in either consumer.

export const formatDateForAPI = (date) => {
  // toISOString() converts to UTC automatically
  // The backend expects ISO 8601 format in UTC
  return date.toISOString();
};

export const getTransactionTimestamp = (transaction) => {
  // Try different timestamp fields in order of preference
  if (transaction.timestampNumber) {
    return parseInt(transaction.timestampNumber);
  }
  if (transaction.timestamp) {
    // Could be a number or ISO string
    const parsed = parseInt(transaction.timestamp);
    return isNaN(parsed) ? new Date(transaction.timestamp).getTime() : parsed;
  }
  if (transaction.timestampISO) {
    return new Date(transaction.timestampISO).getTime();
  }
  // Fallback to current time if no timestamp found
  console.warn('Transaction missing timestamp:', transaction);
  return Date.now();
};

// Helper function to normalize provider names
export const normalizeProviderName = (providerName) => {
  if (!providerName) return null;
  const normalized = providerName.toLowerCase();
  // Replace Rebeltech with Pragmatic Play
  if (normalized === 'rebeltech') {
    return 'Pragmatic Play';
  }
  // Betinovio games are Pragmatic Play titles — surface the brand name and
  // group them under the same provider filter as other Pragmatic games.
  // A raw "pragmatic" provider is normalized to the same label so every
  // Pragmatic-origin game (rebeltech / betinovio / pragmatic) groups together.
  if (normalized === 'betinovio' || normalized === 'pragmatic') {
    return 'Pragmatic Play';
  }
  // Databetrix is only an aggregator, so this label is the fallback used when
  // the underlying studio could not be resolved from the callback.
  if (normalized === 'databetrix') {
    return 'Databetrix';
  }
  // TurboStars - capitalize properly
  if (normalized === 'turbostars') {
    return 'TurboStars';
  }
  // BetConstruct is surfaced as the generic "Sport" label — internal
  // detection still relies on transaction-id / reason substrings, but the
  // brand name is intentionally not displayed to end users.
  if (normalized === 'betconstruct') {
    return 'Sport';
  }
  if (normalized === '3oaks' || normalized === '3-oaks' || normalized === '3oaksgaming') {
    return '3 Oaks Gaming';
  }
  // Capitalize first letter for other providers
  return providerName.charAt(0).toUpperCase() + providerName.slice(1);
};

export const getTransactionProvider = (tx) => {
  const additional = tx?.context?.additionalContext;
  // Databetrix is an aggregator: the round was actually run by the studio in
  // realProvider (Evolution, ...), which is what belongs on the round card.
  if (additional?.provider === 'databetrix' && additional?.realProvider) {
    return additional.realProvider;
  }
  return additional?.provider ||
    tx?.context?.provider ||
    tx?.metadata?.provider;
};

// Helper to detect TurboStars provider
export const isTurboStarsTransaction = (tx) => {
  return tx.context?.additionalContext?.provider === 'turbostars' ||
         tx.context?.reason?.toLowerCase().includes('turbostars');
};

// Internal tracker rows (bc-betstate-*) are not user-visible — always ignore.
export const isBetConstructTrackerRow = (tx) =>
  tx?.transactionId?.startsWith('bc-betstate-') === true;

// Helper to detect BetConstruct provider (excludes tracker rows)
export const isBetConstructTransaction = (tx) => {
  if (!tx || isBetConstructTrackerRow(tx)) return false;
  const provider = getTransactionProvider(tx);
  if (provider && provider.toLowerCase() === 'betconstruct') return true;
  const reason = tx.context?.reason?.toLowerCase() || '';
  if (reason.startsWith('betconstruct')) return true;
  if (tx.transactionId?.startsWith('bc-')) return true;
  return false;
};

// Classify BetConstruct transaction sub-types
export const isBetConstructBet = (tx) => {
  if (!isBetConstructTransaction(tx)) return false;
  if (tx.type?.toLowerCase() !== 'withdrawal') return false;
  // Result settlements have "result" in the reason; bet placements have "bet"
  const reason = tx.context?.reason?.toLowerCase() || '';
  if (reason.includes('result')) return false;
  if (reason.includes('rollback')) return false;
  // Guard against rollback / result ids
  if (tx.transactionId?.startsWith('bc-result-')) return false;
  if (tx.transactionId?.includes('-rollback-')) return false;
  return reason.includes('bet') || tx.transactionId?.startsWith('bc-');
};

export const isBetConstructResult = (tx) => {
  if (!isBetConstructTransaction(tx)) return false;
  if (tx.type?.toLowerCase() !== 'deposit') return false;
  const reason = tx.context?.reason?.toLowerCase() || '';
  return reason.includes('result') ||
         tx.transactionId?.startsWith('bc-result-') ||
         tx.context?.additionalContext?.bcResultAmount !== undefined;
};

export const isBetConstructRollback = (tx) => {
  if (!isBetConstructTransaction(tx)) return false;
  return tx.type?.toLowerCase() === 'rollback' ||
         tx.transactionId?.startsWith('bc-rollback-') ||
         tx.context?.reason?.toLowerCase().includes('rollback');
};

// Map BetConstruct bcBetState to a canonical result label.
// Source: BetConstruct Spring Partner API — BetState enum:
//   1 = Accepted (not resulted / reverted)
//   2 = Returned (void — stake returned)
//   3 = Lost
//   4 = Won
//   5 = Cashed-out
export const bcStateToResult = (state) => {
  const n = Number(state);
  if (n === 1) return 'Pending';
  if (n === 2) return 'Void';
  if (n === 3) return 'Lose';
  if (n === 4) return 'Win';
  if (n === 5) return 'Cashout';
  return null;
};

// Extract the signed delta applied to the wallet by a BetConstruct result row.
// `additionalContext.delta` is authoritative and signed; `amount` is magnitude.
export const bcSignedDelta = (tx) => {
  const d = tx?.context?.additionalContext?.delta;
  if (d !== undefined && d !== null) {
    const n = parseFloat(d);
    if (!isNaN(n)) return n;
  }
  const amt = parseFloat(tx?.amount || 0);
  return isNaN(amt) ? 0 : amt;
};

// Helper to get the base transaction ID (without -settle- or -rollback- suffix)
export const getBaseTransactionId = (transactionId) => {
  if (!transactionId) return null;
  // Remove -settle-xxx or -rollback-xxx suffix
  const settleMatch = transactionId.match(/^(.+?)-settle-/);
  const rollbackMatch = transactionId.match(/^(.+?)-rollback-/);
  if (settleMatch) return settleMatch[1];
  if (rollbackMatch) return rollbackMatch[1];
  return transactionId;
};

export const isSportsTransaction = (tx) => {
  const platform = tx?.platform?.toLowerCase();
  return platform === 'sport' ||
         platform === 'sports' ||
         tx?.context?.product?.toLowerCase() === 'sportsbook';
};

// The id that ties a sports bet's placement to its settlement rows.
export const getSportsGroupingId = (tx) => {
  if (isTurboStarsTransaction(tx)) {
    // For TurboStars: use parentId, originalBetTransactionId, or base transactionId
    return tx.context?.parentId ||
           tx.context?.additionalContext?.originalBetTransactionId ||
           getBaseTransactionId(tx.transactionId);
  }
  // For other sports providers: prioritize betId, then correlationId
  return tx.context?.betId || tx.context?.correlationId;
};

// Split a round's transactions into stake / payout legs and total them.
// `transactions` must already belong to a single round and be sorted oldest
// first. Provider-specific rules apply because TurboStars and BetConstruct
// encode bets and settlements differently from the default sportsbook shape.
export const summarizeRoundTransactions = (transactions) => {
  const isTurboStarsRound = transactions.some(tx => isTurboStarsTransaction(tx));
  const isBetConstructRound = transactions.some(tx => isBetConstructTransaction(tx));

  const bets = transactions.filter(tx => {
    if (isTurboStarsRound) {
      return tx.type?.toLowerCase() === 'withdrawal' &&
             !tx.transactionId?.includes('-settle-') &&
             !tx.transactionId?.includes('-rollback-');
    }
    if (isBetConstructRound) {
      return isBetConstructBet(tx);
    }
    return tx.type?.toLowerCase() === 'bet' ||
           (tx.type?.toLowerCase() === 'withdrawal' && tx.context?.reason?.toLowerCase() === 'bet');
  });

  const wins = transactions.filter(tx =>
    tx.type?.toLowerCase() === 'win' ||
    (tx.type?.toLowerCase() === 'deposit' && tx.context?.reason?.toLowerCase() === 'win')
  );

  const settles = transactions.filter(tx => {
    if (isTurboStarsRound) {
      return tx.type?.toLowerCase() === 'deposit' &&
             (tx.transactionId?.includes('-settle-') ||
              tx.context?.reason?.toLowerCase().includes('settlement') ||
              tx.context?.reason?.toLowerCase().includes('settle'));
    }
    if (isBetConstructRound) {
      return isBetConstructResult(tx);
    }
    return tx.type?.toLowerCase() === 'deposit' && tx.context?.reason?.toLowerCase() === 'settle';
  });

  const resettles = transactions.filter(tx =>
    (tx.type?.toLowerCase() === 'deposit' || tx.type?.toLowerCase() === 'withdrawal') &&
    tx.context?.reason?.toLowerCase() === 'resettle'
  );

  const cancelSettles = transactions.filter(tx =>
    tx.type?.toLowerCase() === 'withdrawal' && tx.context?.reason?.toLowerCase() === 'cancelsettle'
  );

  const rollbacks = transactions.filter(tx =>
    tx.type?.toLowerCase() === 'rollback' ||
    tx.transactionId?.includes('-rollback-')
  );

  const totalBet = bets.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
  const totalWin = wins.reduce((sum, tx) => sum + parseFloat(tx.amount || 0), 0);
  // For BetConstruct, settlement delta is signed (resettlement can subtract).
  // For every other provider, tx.amount is the signed magnitude of the deposit.
  const totalSettle = settles.reduce((sum, tx) => {
    if (isBetConstructRound) return sum + bcSignedDelta(tx);
    return sum + parseFloat(tx.amount || 0);
  }, 0);
  const totalResettle = resettles.reduce((sum, tx) => {
    const amount = parseFloat(tx.amount || 0);
    return sum + (tx.type?.toLowerCase() === 'deposit' ? amount : -amount);
  }, 0);
  const totalCancelSettle = cancelSettles.reduce((sum, tx) => sum - parseFloat(tx.amount || 0), 0);
  const netResult = totalWin + totalSettle + totalResettle + totalCancelSettle - totalBet;

  const hasRollback = rollbacks.length > 0 || bets.some(b => b.rolledBack === 'true' || b.rolledBack === true);
  const turboStarsBetSettled = isTurboStarsRound && bets.some(b => b.settled === 'true' || b.settled === true);
  const hasSettlement = settles.length > 0 || resettles.length > 0 || cancelSettles.length > 0 ||
                        wins.length > 0 || turboStarsBetSettled;

  // Determine settle result
  let settleResult = null;
  if (isTurboStarsRound) {
    const settleWithResult = settles.find(tx => tx.context?.additionalContext?.resultType);
    settleResult = settleWithResult?.context?.additionalContext?.resultType;
    if (!settleResult && bets[0]?.resultType) {
      settleResult = bets[0].resultType;
    }
    if (settleResult === 'won') settleResult = 'Win';
    else if (settleResult === 'lost') settleResult = 'Lose';
    else if (settleResult === 'cashout') settleResult = 'Cashout';
  } else if (isBetConstructRound) {
    // BetConstruct BetState is authoritative. Latest result row wins if
    // multiple (resettlements); we also fall back to 1 (Accepted) if
    // the revert-to-unresulted case happens.
    const latestStateRow = [...settles].sort(
      (a, b) => getTransactionTimestamp(b) - getTransactionTimestamp(a)
    )[0];
    const latestState = Number(latestStateRow?.context?.additionalContext?.bcBetState);
    settleResult = bcStateToResult(latestState);
  } else {
    const allSettleTxs = [...settles, ...resettles].sort((a, b) =>
      getTransactionTimestamp(b) - getTransactionTimestamp(a)
    );
    const latestSettle = allSettleTxs[0];
    settleResult = latestSettle?.context?.extras?.bet?.result || latestSettle?.context?.bet?.result;
  }

  return {
    isTurboStarsRound,
    isBetConstructRound,
    bets,
    wins,
    settles,
    resettles,
    cancelSettles,
    rollbacks,
    totalBet,
    totalWin,
    totalSettle,
    totalResettle,
    totalCancelSettle,
    netResult,
    hasBet: bets.length > 0,
    hasRollback,
    hasSettlement,
    settleResult
  };
};
